import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaPrint, FaSave } from "react-icons/fa";
import Select from "react-select";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios";
import ProgressReportCommissionDetailsModal from "./ProgressReportCommissionDetailsModal";
import {
  PROGRESS_REPORT_COLUMNS,
  PROGRESS_REPORT_FILTER_KEYS,
  PROGRESS_REPORT_SELECT_OPTIONS,
  applyProgressReportFilters,
  applyProgressReportPeriodFilter,
  buildProgressReportAddressFilterOptions,
  buildProgressReportRows,
  cloneProgressReportRows,
  getProgressReportColumnLabel,
  getProgressReportFilterOptions,
  getProgressReportPeriodDateFieldLabel,
  getProgressReportSelectOptions,
  PROGRESS_REPORT_PERIOD_DATE_FIELDS,
  PROGRESS_REPORT_PARTIAL_MATCH_FILTER_KEYS,
  PROGRESS_REPORT_TYPEABLE_FILTER_KEYS,
  buildProgressReportTradeFilterOptions,
  getProgressReportColumnCellClass,
  getProgressReportInvoiceDateCellClass,
  progressReportDateForInput,
  progressReportDateFromInput,
} from "../utils/progressReportUtils";
import { saveProgressReportRows } from "../utils/progressReportSave";
import { openProgressReportPrintWindowFromRows } from "../utils/progressReportPrintHtml";

const tradeFilterSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "34px",
    fontSize: "0.75rem",
    borderColor: "#d1d5db",
    boxShadow: "none",
    "&:hover": { borderColor: "#1d4ed8" },
  }),
  menu: (base) => ({ ...base, fontSize: "0.75rem", zIndex: 80 }),
  menuPortal: (base) => ({ ...base, zIndex: 80 }),
};

const cellInputClass =
  "w-full min-w-[4.5rem] pl-2 pr-2 py-1.5 text-xs border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-blue-600 focus:outline-none";

const cellDateClass =
  "w-full min-w-[9rem] pl-2 pr-8 py-1.5 text-xs border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-blue-600 focus:outline-none";

const cellInputMinWidth = {
  action: "min-w-0",
  notes: "min-w-0",
};

const EDITABLE_KEYS = new Set([
  "tradeCategory",
  "compliance",
  "action",
  "notes",
  "followUpDate",
]);

const BLANK_SELECT_KEYS = new Set(["tradeCategory", "compliance"]);

const cellSelectClass =
  "w-full min-w-[6.5rem] text-xs border-0 bg-transparent py-1.5 pl-1 pr-6 focus:bg-white focus:ring-1 focus:ring-blue-600 focus:outline-none appearance-none cursor-pointer";

function ReportCell({ col, row, onChange, allRows }) {
  const value = row[col.key] ?? "";

  if (col.edit === "select" || BLANK_SELECT_KEYS.has(col.key)) {
    const options =
      getProgressReportSelectOptions(col.key, allRows) ||
      PROGRESS_REPORT_SELECT_OPTIONS[col.key] ||
      [];
    const displayValue = String(value || "").trim();
    return (
      <select
        value={displayValue}
        onChange={(e) => onChange(row.rowKey, col.key, e.target.value)}
        className={`${cellSelectClass} ${
          !displayValue ? "text-transparent" : "text-gray-800"
        }`}
        aria-label={col.label}
      >
        <option value="" hidden />
        {options
          .filter((o) => o.value !== "")
          .map((o) => (
            <option key={o.value} value={o.value} className="text-gray-800">
              {o.label}
            </option>
          ))}
      </select>
    );
  }

  if (!EDITABLE_KEYS.has(col.key)) {
    return (
      <span
        className={`block px-2 py-1.5 text-xs text-gray-800 ${
          col.key === "agentName" ? "truncate" : "whitespace-nowrap"
        }`}
        title={value || undefined}
      >
        {value || "—"}
      </span>
    );
  }

  if (col.key === "followUpDate") {
    const iso = progressReportDateForInput(value);
    return (
      <input
        type="date"
        value={iso}
        onChange={(e) =>
          onChange(
            row.rowKey,
            col.key,
            progressReportDateFromInput(e.target.value),
          )
        }
        className={cellDateClass}
      />
    );
  }

  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(row.rowKey, col.key, e.target.value)}
      className={`${cellInputClass} ${cellInputMinWidth[col.key] || ""}`}
      title={value || undefined}
    />
  );
}

const ProgressReportModal = ({
  isOpen,
  onClose,
  trades,
  loading,
  onTradesSaved,
}) => {
  const [rows, setRows] = useState([]);
  const [baselineRows, setBaselineRows] = useState([]);
  const [filters, setFilters] = useState({});
  const [filterColumn, setFilterColumn] = useState("");
  const [periodFilter, setPeriodFilter] = useState({
    dateField: "",
    fromDate: "",
    toDate: "",
  });
  const [saving, setSaving] = useState(false);
  const [commissionDetailsOpen, setCommissionDetailsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setRows([]);
      setBaselineRows([]);
      setFilters({});
      setFilterColumn("");
      setPeriodFilter({ dateField: "", fromDate: "", toDate: "" });
      return;
    }
    const built = buildProgressReportRows(trades || []);
    const cloned = cloneProgressReportRows(built);
    setRows(cloned);
    setBaselineRows(cloneProgressReportRows(built));
  }, [isOpen, trades]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const filteredRows = useMemo(() => {
    const columnFiltered = applyProgressReportFilters(rows, filters);
    return applyProgressReportPeriodFilter(columnFiltered, periodFilter);
  }, [rows, filters, periodFilter]);

  const tradeFilterOptions = useMemo(
    () => buildProgressReportTradeFilterOptions(trades),
    [trades],
  );

  const addressFilterOptions = useMemo(
    () => buildProgressReportAddressFilterOptions(rows),
    [rows],
  );

  const selectedTradeFilterOption = useMemo(() => {
    const q = String(filters.tradeNumber || "").trim();
    if (!q) return null;
    return tradeFilterOptions.find((o) => o.value === q) || null;
  }, [tradeFilterOptions, filters.tradeNumber]);

  const selectedAddressFilterOption = useMemo(() => {
    const q = String(filters.address || "").trim();
    if (!q) return null;
    return addressFilterOptions.find((o) => o.value === q) || null;
  }, [addressFilterOptions, filters.address]);

  const handleCellChange = useCallback((rowKey, columnKey, value) => {
    setRows((prev) =>
      prev.map((row) =>
        row.rowKey === rowKey ? { ...row, [columnKey]: value } : row,
      ),
    );
  }, []);

  const handleFilterColumnChange = (columnKey) => {
    setFilterColumn(columnKey);
    if (!columnKey) return;
    setFilters((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
  };

  const filterUsesTradeSelect =
    filterColumn && PROGRESS_REPORT_TYPEABLE_FILTER_KEYS.has(filterColumn);

  const filterUsesAddressInput =
    filterColumn && PROGRESS_REPORT_PARTIAL_MATCH_FILTER_KEYS.has(filterColumn);

  const handleFilterValueChange = (value) => {
    if (!filterColumn) return;
    setFilters((prev) => {
      const next = { ...prev };
      if (!value) delete next[filterColumn];
      else next[filterColumn] = value;
      return next;
    });
  };

  const removeFilter = (columnKey) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    if (filterColumn === columnKey) setFilterColumn("");
  };

  const handleSave = async () => {
    if (!rows.length) {
      toast.error("No rows to save.");
      return;
    }
    setSaving(true);
    try {
      const { dirtyCount, savedCount, skippedInvalid } =
        await saveProgressReportRows(rows, baselineRows, trades, axiosInstance);
      if (dirtyCount === 0) {
        toast.info("No changes to save.");
        return;
      }
      if (savedCount === 0) {
        toast.error(
          skippedInvalid > 0
            ? "Could not save: invalid trade id on one or more rows. Reload the report and try again."
            : "Changes could not be saved. Restart the backend and try again.",
        );
        return;
      }
      if (onTradesSaved) await onTradesSaved();
      toast.success(`Saved ${savedCount} trade${savedCount === 1 ? "" : "s"}.`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save progress report changes.");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    if (!filteredRows.length) {
      toast.error("No rows to print.");
      return;
    }
    const opened = openProgressReportPrintWindowFromRows(filteredRows);
    if (!opened) {
      toast.error("Pop-up blocked. Allow pop-ups to print the report.");
    }
  };

  if (!isOpen) return null;

  const filterActiveCount = Object.values(filters).filter(
    (v) => v != null && String(v).trim() !== "",
  ).length;

  const periodFilterActive =
    periodFilter.dateField && (periodFilter.fromDate || periodFilter.toDate);

  const handlePeriodFilterChange = (field, value) => {
    setPeriodFilter((prev) => ({ ...prev, [field]: value }));
  };

  const clearPeriodFilter = () => {
    setPeriodFilter({ dateField: "", fromDate: "", toDate: "" });
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-4 bg-black/50"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="progress-report-modal-title"
        className="bg-white rounded-xl shadow-2xl w-full max-w-[min(96vw,1400px)] max-h-[min(92vh,900px)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 shrink-0">
          <div className="min-w-0 flex-1">
            <h2
              id="progress-report-modal-title"
              className="text-lg font-bold text-gray-900"
            >
              Progress Report
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Trade Category and Compliance use dropdowns (blank until you
              select). Invoice Date: listing-side trades show commission invoice
              sent (blue); co-operating trades show commission invoice received.
              Open TRS and Closed TRS dates also come from Progress Tracker.
              Commission Received uses the EFT issue date for #1000–1999 (Real
              Estate Trust) or #4000+ only. AR Amount, Invoice Amount, and
              Outside Broker come from the Commission tab. Save updates
              category, compliance, action, notes, and follow up.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <span className="text-xl leading-none">&times;</span>
          </button>
        </div>

        {!loading && rows.length > 0 && (
          <div className="px-4 py-3 border-b border-gray-100 shrink-0 space-y-3">
            <div>
              <p className="text-xs font-medium text-gray-600 mb-2">
                Filter by period
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-0.5 min-w-[11rem]">
                  <span className="text-[10px] font-medium text-gray-500">
                    Date type
                  </span>
                  <select
                    value={periodFilter.dateField}
                    onChange={(e) =>
                      handlePeriodFilterChange("dateField", e.target.value)
                    }
                    className="rounded border border-gray-300 text-xs py-1.5 pl-2 pr-7 bg-white min-w-[11rem]"
                  >
                    <option value="">Select date type…</option>
                    {PROGRESS_REPORT_PERIOD_DATE_FIELDS.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-0.5 min-w-[9rem]">
                  <span className="text-[10px] font-medium text-gray-500">
                    From
                  </span>
                  <input
                    type="date"
                    value={periodFilter.fromDate}
                    onChange={(e) =>
                      handlePeriodFilterChange("fromDate", e.target.value)
                    }
                    disabled={!periodFilter.dateField}
                    className="rounded border border-gray-300 text-xs py-1.5 px-2 bg-white disabled:bg-gray-100 disabled:text-gray-400"
                  />
                </label>
                <label className="flex flex-col gap-0.5 min-w-[9rem]">
                  <span className="text-[10px] font-medium text-gray-500">
                    To
                  </span>
                  <input
                    type="date"
                    value={periodFilter.toDate}
                    onChange={(e) =>
                      handlePeriodFilterChange("toDate", e.target.value)
                    }
                    disabled={!periodFilter.dateField}
                    className="rounded border border-gray-300 text-xs py-1.5 px-2 bg-white disabled:bg-gray-100 disabled:text-gray-400"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => setCommissionDetailsOpen(true)}
                  disabled={loading || !trades?.length}
                  className="inline-flex items-center px-3 py-1.5 rounded-md border border-blue-900 text-blue-900 bg-white text-xs font-medium hover:bg-blue-50 disabled:opacity-50 whitespace-nowrap"
                >
                  Commission Details
                </button>
                {periodFilterActive && (
                  <button
                    type="button"
                    onClick={clearPeriodFilter}
                    className="text-xs text-blue-800 hover:underline pb-1.5"
                  >
                    Clear period
                  </button>
                )}
              </div>
              {periodFilterActive && (
                <p className="mt-2 text-xs text-blue-900">
                  Showing trades with{" "}
                  <span className="font-medium">
                    {getProgressReportPeriodDateFieldLabel(
                      periodFilter.dateField,
                    )}
                  </span>
                  {periodFilter.fromDate && periodFilter.toDate
                    ? ` from ${periodFilter.fromDate} to ${periodFilter.toDate}`
                    : periodFilter.fromDate
                      ? ` on or after ${periodFilter.fromDate}`
                      : ` on or before ${periodFilter.toDate}`}
                </p>
              )}
            </div>

            <div>
              <p className="text-xs font-medium text-gray-600 mb-2">Filter</p>
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-0.5 min-w-[10rem]">
                  <span className="text-[10px] font-medium text-gray-500">
                    Column
                  </span>
                  <select
                    value={filterColumn}
                    onChange={(e) => handleFilterColumnChange(e.target.value)}
                    className="rounded border border-gray-300 text-xs py-1.5 pl-2 pr-7 bg-white min-w-[10rem]"
                  >
                    <option value="">Select column…</option>
                    {PROGRESS_REPORT_FILTER_KEYS.map((key) => (
                      <option key={key} value={key}>
                        {getProgressReportColumnLabel(key)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-0.5 min-w-[12rem] flex-1 max-w-md">
                  <span className="text-[10px] font-medium text-gray-500">
                    Value
                  </span>
                  {filterUsesAddressInput ? (
                    <Select
                      inputId="progress-report-address-filter"
                      options={addressFilterOptions}
                      value={selectedAddressFilterOption}
                      inputValue={filters.address || ""}
                      onInputChange={(value, meta) => {
                        if (meta.action === "input-change") {
                          handleFilterValueChange(value);
                        }
                      }}
                      onChange={(opt) =>
                        handleFilterValueChange(opt?.value || "")
                      }
                      isClearable
                      isSearchable
                      isDisabled={!filterColumn}
                      placeholder="Type or select address…"
                      noOptionsMessage={() => "No matching addresses"}
                      styles={tradeFilterSelectStyles}
                      className="w-full text-xs"
                      classNamePrefix="progress-report-address-filter"
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      menuPosition="fixed"
                    />
                  ) : filterUsesTradeSelect ? (
                    <Select
                      inputId="progress-report-trade-filter"
                      options={tradeFilterOptions}
                      value={selectedTradeFilterOption}
                      onChange={(opt) =>
                        handleFilterValueChange(opt?.value || "")
                      }
                      isClearable
                      isSearchable
                      isDisabled={!filterColumn}
                      placeholder="Search or select a trade…"
                      noOptionsMessage={() => "No matching trades"}
                      styles={tradeFilterSelectStyles}
                      className="w-full text-xs"
                      classNamePrefix="progress-report-trade-filter"
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      menuPosition="fixed"
                    />
                  ) : (
                    <select
                      value={filterColumn ? filters[filterColumn] || "" : ""}
                      onChange={(e) => handleFilterValueChange(e.target.value)}
                      disabled={!filterColumn}
                      className="rounded border border-gray-300 text-xs py-1.5 pl-2 pr-7 bg-white w-full disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      {!filterColumn ? (
                        <option value="">Select a column first</option>
                      ) : (
                        getProgressReportFilterOptions(filterColumn, rows).map(
                          (opt) => (
                            <option
                              key={`${filterColumn}-${opt.value}`}
                              value={opt.value}
                            >
                              {opt.label}
                            </option>
                          ),
                        )
                      )}
                    </select>
                  )}
                </label>
              </div>
              {filterActiveCount > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {Object.entries(filters)
                    .filter(([, v]) => v != null && String(v).trim() !== "")
                    .map(([key, value]) => (
                      <span
                        key={key}
                        className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2.5 py-0.5 text-xs text-blue-900"
                      >
                        <span className="font-medium">
                          {getProgressReportColumnLabel(key)}:
                        </span>
                        <span>
                          {key === "tradeNumber"
                            ? selectedTradeFilterOption?.label ||
                              tradeFilterOptions.find((o) => o.value === value)
                                ?.label ||
                              value
                            : key === "address"
                              ? value
                              : value}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFilter(key)}
                          className="ml-0.5 text-blue-700 hover:text-blue-950 leading-none"
                          aria-label={`Remove ${getProgressReportColumnLabel(key)} filter`}
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  <button
                    type="button"
                    onClick={() => {
                      setFilters({});
                      setFilterColumn("");
                      clearPeriodFilter();
                    }}
                    className="text-xs text-blue-800 hover:underline"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-auto px-4 py-3 min-h-0">
          {loading ? (
            <p className="text-sm text-gray-500 py-12 text-center">
              Loading trades…
            </p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500 py-12 text-center">
              No trades to show.
            </p>
          ) : filteredRows.length === 0 ? (
            <p className="text-sm text-gray-500 py-12 text-center">
              No trades match the selected filters.
            </p>
          ) : (
            <table className="min-w-full table-fixed border border-gray-300 text-xs">
              <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm">
                <tr>
                  {PROGRESS_REPORT_COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className={`border border-gray-300 px-2 py-2 text-left font-semibold text-gray-800 whitespace-nowrap ${getProgressReportColumnCellClass(col.key)}`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.rowKey} className="hover:bg-gray-50/80">
                    {PROGRESS_REPORT_COLUMNS.map((col) => (
                      <td
                        key={col.key}
                        className={`border border-gray-200 p-0 align-top overflow-hidden ${getProgressReportColumnCellClass(col.key)} ${getProgressReportInvoiceDateCellClass(col.key, row)}`}
                      >
                        <ReportCell
                          col={col}
                          row={row}
                          onChange={handleCellChange}
                          allRows={rows}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 shrink-0 flex flex-wrap items-center justify-between gap-3 bg-gray-50 rounded-b-xl">
          <span className="text-sm text-gray-600">
            {filterActiveCount > 0 || periodFilterActive
              ? `${filteredRows.length} of ${rows.length} trades`
              : `${rows.length} trade${rows.length === 1 ? "" : "s"}`}
          </span>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || saving || !rows.length}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-green-700 bg-green-700 text-white text-sm font-medium hover:bg-green-800 disabled:opacity-50"
            >
              <FaSave /> {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={loading || !filteredRows.length}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-900 text-white text-sm font-medium hover:bg-blue-950 disabled:opacity-50"
            >
              <FaPrint /> Print
            </button>
          </div>
        </div>
      </div>

      <ProgressReportCommissionDetailsModal
        isOpen={commissionDetailsOpen}
        onClose={() => setCommissionDetailsOpen(false)}
        trades={trades}
      />
    </div>
  );
};

export default ProgressReportModal;
